import { Component, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { UsuarioService } from 'src/app/services/usuario.service';
import { API_URI} from 'src/environments/environment';
import { LoadingService } from 'src/app/services/loading.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-my-account',
    templateUrl: './my-account.component.html',
    styleUrls: ['./my-account.component.css'],
    standalone: false
})
export class MyAccountComponent {
  private _usuarioService = inject(UsuarioService);
  private _authService = inject(AuthService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);

  // estado
  error   = signal<string>('');
  message = signal<string>('');
  connect_mercadopago = signal<boolean>(false);

  usuario = computed(() => this._usuarioService.usuario());
  cliente = computed(() => this._usuarioService.cliente?.());
  comercio = computed(() => this._usuarioService.comercio?.());
  prestador = computed(() => this._usuarioService.prestador?.());

  id_perfil = computed(() => this.usuario()?.id_perfil);
  email = computed(() => this.usuario()?.x_email ?? '');

  preview_img_avatar = computed(() => {
    const img = this.usuario()?.x_img_avatar;
    return img ? `${API_URI}uploads/images/small-${img}` : '';
  });

  nombre = computed(() => {
    const idPerfil = this.id_perfil();

    let nombreCompleto = '';

    if (idPerfil === 4) {
      const com = this.comercio();

      nombreCompleto = [
        com?.x_nombre_fantasia,
        com?.x_razon_social
      ]
        .filter(Boolean)
        .join(' - ')
        .trim();

    } else if (idPerfil === 3) {
      const p = this.prestador();

      nombreCompleto = [
        p?.x_nombre,
        p?.x_apellido
      ]
        .filter(Boolean)
        .join(' ')
        .trim();

    } else {
      const c = this.cliente();

      nombreCompleto = [
        c?.x_nombre,
        c?.x_apellido
      ]
        .filter(Boolean)
        .join(' ')
        .trim();
    }

    return nombreCompleto || 'Nombre Usuario';
  });

  // == helpers ==
  private addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    return d;
  }

  private isMpTokenValid(expiresAt?: Date | string | null): boolean {
    if (!expiresAt) return false;
    const exp = new Date(expiresAt);
    if (isNaN(exp.getTime())) return false;
    // válido si “hoy” < fecha de expiración
    return new Date() < exp;
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    try {
      await this._loadingService.run(async () => {
        // 1) asegura sesión
        await this._authService.ensureSession();

        const currentUserId = this.usuario()?.id;

        if (currentUserId) {
          const usuarioActualizado = await firstValueFrom(
            this._usuarioService.getUsuarioXId(currentUserId)
          );

          if (usuarioActualizado) {
            this._usuarioService.usuario.set(usuarioActualizado);
          }
        }

        const u = this.usuario();

        if (u?.id_perfil === 3 || u?.id_perfil === 4) {
          const exp = u?.d_mp_expires_in
            ? new Date(u.d_mp_expires_in)
            : null;

          if (exp && !isNaN(exp.getTime())) {
            const diezDiasAntes = this.addDays(new Date(), 10);

            if (diezDiasAntes > exp && u?.x_mp_refresh_token) {
              try {
                await firstValueFrom(
                  this._usuarioService.updateMercadoPagoRefreshToken(
                    u.id!,
                    u.x_mp_refresh_token
                  )
                );

                this.message.set('Se actualizó el Refresh Token de Mercado Pago.');
              } catch (e) {
                console.error('Error al refrescar token de MP', e);
                this.error.set('Error al obtener el Refresh Token de Mercado Pago');
              }
            }
          }

          // estado de conexión
          this.connect_mercadopago.set(
            Boolean(u?.x_mp_access_token) && this.isMpTokenValid(u?.d_mp_expires_in)
          );
        }
      });
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
      this.router.navigate(['/sign-in']);
    }
  }

  async mercadopago(): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        const userId = this.usuario()?.id;

        if (!userId) {
          this.error.set('No se encontró el usuario.');
          return;
        }
        const url = await firstValueFrom(this._usuarioService.getMercadoPagoUrlAutorizacion(userId));
        // backend devuelve la URL codificada
        window.open(decodeURIComponent(url), '_self');
      } catch (e) {
        console.error('Error al obtener la URL de autorización de MercadoPago', e);
        this.error.set('No se pudo iniciar la vinculación con Mercado Pago.');
      }
    });
  }

  async onMercadoPagoClick(): Promise<void> {
    this.error.set('');
    this.message.set('');

    if (!this.connect_mercadopago()) {
      await this.router.navigate(['/my-account-mercadopago']);
      return;
    }

    const confirmar = confirm('¿Querés desvincular tu cuenta de Mercado Pago?');

    if (!confirmar) return;

    await this.desvincularMercadoPago();
  }

  async desvincularMercadoPago(): Promise<void> {
    await this._loadingService.run(async () => {
      try {
        const userId = this.usuario()?.id!;

        await firstValueFrom(
          this._usuarioService.desvincularMercadoPago(userId)
        );

        const u = this.usuario();

        this._usuarioService.usuario.set({
          ...u,
          x_mp_code: null,
          x_mp_access_token: null,
          x_mp_refresh_token: null,
          d_mp_expires_in: null,
        });

        this.connect_mercadopago.set(false);
        this.message.set('Cuenta de Mercado Pago desvinculada correctamente.');
      } catch (e) {
        console.error(e);
        this.error.set('No se pudo desvincular Mercado Pago.');
      }
    });
  }

  back(): void {
    this._location.back();
  }
}
